import { useState, useEffect, useCallback, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";

// Evento customizado para sincronizar mudanças entre instâncias do hook
const MUTED_GROUPS_CHANGED_EVENT = "mutedGroupsChanged";

/**
 * 🔇 Hook para gerenciar grupos silenciados por atendente
 * 
 * Armazena preferências no localStorage separado por userId
 * para que cada atendente tenha sua própria lista de grupos silenciados.
 * 
 * @returns {Object} Funções para gerenciar grupos silenciados
 */
const useMutedGroups = () => {
  const { user } = useContext(AuthContext);
  const [mutedGroups, setMutedGroups] = useState([]);

  // Chave do localStorage baseada no userId
  const getStorageKey = useCallback(() => {
    return user?.id ? `mutedGroups_${user.id}` : null;
  }, [user?.id]);

  // Função para carregar do localStorage
  const loadFromStorage = useCallback(() => {
    const storageKey = getStorageKey();
    if (!storageKey) {
      return [];
    }

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.warn("⚠️ [useMutedGroups] Erro ao carregar grupos silenciados:", err);
    }
    return [];
  }, [getStorageKey]);

  // Carregar grupos silenciados do localStorage na inicialização
  useEffect(() => {
    setMutedGroups(loadFromStorage());
  }, [loadFromStorage]);

  // 🔄 Sincronizar mudanças entre instâncias do hook (mesma página)
  useEffect(() => {
    const handleMutedGroupsChanged = (event) => {
      const storageKey = getStorageKey();
      // Verificar se o evento é para o mesmo usuário
      if (event.detail?.storageKey === storageKey) {
        setMutedGroups(event.detail.groups || []);
      }
    };

    // Ouvir evento customizado para sincronização intra-página
    window.addEventListener(MUTED_GROUPS_CHANGED_EVENT, handleMutedGroupsChanged);

    // Ouvir evento storage para sincronização entre abas
    const handleStorageChange = (event) => {
      const storageKey = getStorageKey();
      if (event.key === storageKey) {
        setMutedGroups(loadFromStorage());
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(MUTED_GROUPS_CHANGED_EVENT, handleMutedGroupsChanged);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [getStorageKey, loadFromStorage]);

  // Salvar no localStorage e notificar outras instâncias
  const saveToStorage = useCallback((groups) => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(groups));
      
      // 🔄 Disparar evento customizado para sincronizar outras instâncias na mesma página
      window.dispatchEvent(new CustomEvent(MUTED_GROUPS_CHANGED_EVENT, {
        detail: { storageKey, groups }
      }));
    } catch (err) {
      console.warn("⚠️ [useMutedGroups] Erro ao salvar grupos silenciados:", err);
    }
  }, [getStorageKey]);

  /**
   * Converte ticketId para número válido
   * @param {any} ticketId 
   * @returns {number|null} - Retorna número válido ou null se inválido
   */
  const toValidId = useCallback((ticketId) => {
    if (ticketId === null || ticketId === undefined) return null;
    const numericId = Number(ticketId);
    // Verificar se é um número válido (não NaN e finito)
    if (Number.isNaN(numericId) || !Number.isFinite(numericId)) return null;
    return numericId;
  }, []);

  /**
   * Verifica se um grupo está silenciado
   * @param {number} ticketId - ID do ticket do grupo
   * @returns {boolean}
   */
  const isGroupMuted = useCallback((ticketId) => {
    const numericId = toValidId(ticketId);
    if (numericId === null) return false;
    return mutedGroups.includes(numericId);
  }, [mutedGroups, toValidId]);

  /**
   * Silencia um grupo
   * @param {number} ticketId - ID do ticket do grupo
   */
  const muteGroup = useCallback((ticketId) => {
    const numericId = toValidId(ticketId);
    if (numericId === null) {
      return;
    }
    
    if (mutedGroups.includes(numericId)) return; // Já está silenciado

    const newMutedGroups = [...mutedGroups, numericId];
    setMutedGroups(newMutedGroups);
    saveToStorage(newMutedGroups);
  }, [mutedGroups, saveToStorage, toValidId]);

  /**
   * Dessilencia um grupo
   * @param {number} ticketId - ID do ticket do grupo
   */
  const unmuteGroup = useCallback((ticketId) => {
    const numericId = toValidId(ticketId);
    if (numericId === null) {
      return;
    }

    if (!mutedGroups.includes(numericId)) return; // Não está silenciado

    const newMutedGroups = mutedGroups.filter(id => id !== numericId);
    setMutedGroups(newMutedGroups);
    saveToStorage(newMutedGroups);
  }, [mutedGroups, saveToStorage, toValidId]);

  /**
   * Alterna o estado de silenciamento
   * @param {number} ticketId - ID do ticket do grupo
   */
  const toggleMute = useCallback((ticketId) => {
    if (isGroupMuted(ticketId)) {
      unmuteGroup(ticketId);
    } else {
      muteGroup(ticketId);
    }
  }, [isGroupMuted, muteGroup, unmuteGroup]);

  /**
   * Retorna a lista de IDs de grupos silenciados
   * @returns {number[]}
   */
  const getMutedGroups = useCallback(() => {
    return [...mutedGroups];
  }, [mutedGroups]);

  return {
    mutedGroups,
    isGroupMuted,
    muteGroup,
    unmuteGroup,
    toggleMute,
    getMutedGroups,
  };
};

export default useMutedGroups;
